<template>
  <!-- Card 86akqwmzy: aba própria para os treinamentos dos professores
       (módulos compartilhados só com professores -- card 86akptec0). -->
  <div class="course-page">
    <div class="course-page__inner">
      <CourseHeader
        :eyebrow="$t('courseIndex.teacherEyebrow')"
        :title="$t('courseIndex.trainingsTitle')"
        :subtitle="$t('courseIndex.trainingsSub')"
        :show-ramp="false"
      />

      <CourseFinder
        v-if="!loading && trainings.length"
        :search.sync="search"
        :summary="summary"
      />

      <!-- carregando -->
      <page-skeleton v-if="loading" variant="cards" class="mt-4" />

      <template v-else>
        <LessonSearchResults
          v-if="searching"
          :results="foundLessons"
          @open="openLesson"
        />

        <section v-if="visibleTrainings.length > 0" class="msection">
          <p v-if="searching" class="msection__label">
            {{ $t("courseIndex.trainings") }}
          </p>
          <div class="mgrid">
            <ModuleCard
              v-for="(item, index) in visibleTrainings"
              :key="item.id || index"
              :course="item"
              role="teacher"
              @open="goToTraining"
            />
          </div>
        </section>

        <section
          v-if="!searching && progress && progress.totalLessons"
          class="msection"
        >
          <p class="msection__label">{{ $t("courseIndex.yourProgress") }}</p>
          <PartnerTrainingProgress :progress="progress" />
        </section>

        <!-- vazio -->
        <div v-if="!trainings.length" class="course-page__state">
          <span>{{ $t("courseIndex.noTrainings") }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import pitchPartnerApi from "@/shared/pitchPartnerApi";
import {
  isTrainingModule,
  normalizeSearch,
  findLessons,
} from "@/shared/courseTiers";
import CourseHeader from "@/components/connect/course/CourseHeader.vue";
import CourseFinder from "@/components/connect/course/CourseFinder.vue";
import LessonSearchResults from "@/components/connect/course/LessonSearchResults.vue";
import ModuleCard from "@/components/connect/course/ModuleCard.vue";
import PartnerTrainingProgress from "@/components/connect/partner/PartnerTrainingProgress.vue";

export default {
  name: "PartnerTrainings",
  components: {
    CourseHeader,
    CourseFinder,
    LessonSearchResults,
    ModuleCard,
    PartnerTrainingProgress,
  },
  async created() {
    try {
      const res = await pitchPartnerApi.listCourseContent();
      this.content = res.data.content || [];
    } catch (error) {
      this.$root.$emit("display-alert", {
        text: this.$t("courseIndex.trainingsFailed"),
        type: 1,
      });
    }

    // A evolução é complementar: se falhar, os treinamentos continuam
    // abrindo, só sem a contagem de concluídas.
    if (this.content.some((item) => isTrainingModule(item))) {
      try {
        const res = await pitchPartnerApi.getTrainingProgress();
        this.progress = res.data;
      } catch (error) {
        this.progress = null;
      }
    }

    this.loading = false;
  },
  data() {
    return {
      loading: true,
      content: [],
      progress: null,
      search: "",
    };
  },
  computed: {
    // Treinamentos com a contagem de concluídas mesclada (ids comparados como
    // string -- nunca Number()).
    trainings() {
      const byId = new Map(
        ((this.progress && this.progress.trainings) || []).map((item) => [
          String(item.id),
          item,
        ])
      );

      return this.content
        .filter((item) => isTrainingModule(item))
        .map((item) => {
          const stats = byId.get(String(item.id));
          return stats
            ? { ...item, completedCount: stats.completedCount }
            : item;
        });
    },
    searching() {
      return !!normalizeSearch(this.search);
    },
    foundLessons() {
      return findLessons(this.trainings, this.search);
    },
    visibleTrainings() {
      if (!this.searching) return this.trainings;

      const term = normalizeSearch(this.search);
      const withLesson = new Set(
        this.foundLessons.map((item) => String(item.module.id))
      );
      return this.trainings.filter(
        (item) =>
          withLesson.has(String(item.id)) ||
          normalizeSearch(item.title).includes(term)
      );
    },
    summary() {
      const p = this.progress;
      if (p && p.totalLessons) {
        return this.$t("courseIndex.trainingsSummary", {
          done: p.completedCount || 0,
          total: p.totalLessons,
          percent: Math.round(Number(p.completedPercentage) || 0),
        });
      }
      const total = this.trainings.length;
      return this.$tc("courseIndex.trainingCount", total, { count: total });
    },
  },
  methods: {
    goToTraining(item) {
      this.$router.push({
        path: "/connect/professor/treinamentos/" + item.id,
      });
    },
    openLesson({ module, lesson }) {
      this.$router.push({
        path: `/connect/professor/treinamentos/${module.id}/${lesson.id}`,
      });
    },
  },
};
</script>

<style scoped>
.course-page {
  padding: clamp(20px, 4vw, 44px) clamp(16px, 4vw, 40px) 72px;
}
.course-page__inner {
  max-width: 1180px;
  margin: 0 auto;
}
.msection + .msection {
  margin-top: 44px;
}
.msection__label {
  margin: 0 0 18px;
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--dk-faint, #9a96a6);
}
.mgrid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}
@media (max-width: 900px) {
  .mgrid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .mgrid {
    grid-template-columns: 1fr;
  }
}
.course-page__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  font-family: "Poppins", sans-serif;
  color: var(--dk-ink-soft, #6e6a79);
}
</style>
